// src/Pages/PreOrder/PreOrderUpload/validationRules/fieldValidationRules.js

// Define recurring patterns
const PATTERNS = {
  HEBREW_ONLY: /^[^\u0590-\u05FF\s]+$/, // Hebrew letters and spaces only
  ENGLISH_ONLY: /^[a-zA-Z\s]+$/, // English letters and spaces only
  LETTERS_ONLY: /^[\u0590-\u05FFa-zA-Z\s]+$/, // All letters and spaces
  PHONE_ISRAEL: /^05\d{8}$/, // Israeli format without dash
  PHONE_ISRAEL_WITH_DASH: /^05\d-\d{7}$/, // Israeli format with dash
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, // Standard email
};

// Define general messages
const MESSAGES = {
  REQUIRED: "שדה זה נדרש למילוי",
  TEXT_RULES: "השדה חייב להכיל טקסט תקין", // Field must contain valid text
  FULL_NAME_RULES: "הכנס שם פרטי ושם משפחה מופרדים ברווח (3 רווחים לכל היותר)",
  PHONE_RULES: "הכנס מספר טלפון בפורמט: 05X-XXXXXXX או 05XXXXXXXXX",
  EMAIL_RULES: "הכנס כתובת אימייל תקינה",
  NUMBER_RULES: "השדה חייב להכיל מספרים בלבד",
};

// Define rules for each field type
const FIELD_VALIDATION_RULES = {
  text: {
    validate: (value) => {
      if (!value || value.trim() === "") return null;
      // Removal of PATTERNS.LETTERS_ONLY check
      // Now anything can be entered
      return null;
    },
    transform: (value) => value.trim(),
  },

  fullName: {
    validate: (value) => {
      if (!value || value.trim() === "") return null;

      // Check that there are no dots in the name
      if (value.includes(".")) {
        return "שם לא יכול להכיל נקודות";
      }

      // Check that it does not start with a space
      if (value.startsWith(" ")) {
        return "השם לא יכול להתחיל ברווח";
      }

      // Check for double spaces
      if (value.includes("  ")) {
        return "לא ניתן להזין רווחים כפולים";
      }

      // Check that it does not end with a space
      if (value.endsWith(" ")) {
        return "יש להוסיף טקסט אחרי הרווח";
      }

      // Count single spaces
      const spaces = (value.match(/\s/g) || []).length;

      if (spaces === 0) {
        return "יש להזין לפחות שם פרטי ושם משפחה מופרדים ברווח";
      }

      if (spaces > 3) {
        return "מקסימום 4 מילים (3 רווחים)";
      }

      // Check that each part between spaces contains text
      const parts = value.split(" ");
      for (let part of parts) {
        if (part.trim() === "") {
          return "כל חלק בשם חייב להכיל טקסט";
        }
      }

      return null;
    },
    transform: (value) => {
      if (!value) return value;

      // Prevent adding a double space
      const currentLength = value.length;
      const lastChar = value[currentLength - 1];
      const secondLastChar = value[currentLength - 2];

      // If the last and second to last characters are spaces, return the value without the last space
      if (lastChar === " " && secondLastChar === " ") {
        return value.slice(0, -1);
      }

      // Prevent dots
      if (lastChar === ".") {
        return value.slice(0, -1);
      }

      return value;
    },
  },

  firstName: {
    validate: (value) => {
      if (!value || value.trim() === "") return null;

      if (value.includes(".")) {
        return "שם לא יכול להכיל נקודות";
      }

      if (value.startsWith(" ")) {
        return "השם לא יכול להתחיל ברווח";
      }

      if (value.endsWith(" ")) {
        return "השם לא יכול להסתיים ברווח";
      }

      if (value.includes("  ")) {
        return "לא ניתן להזין רווחים כפולים";
      }

      return null;
    },
    transform: (value) => {
      if (!value) return value;
      if (value.endsWith("  ")) {
        return value.slice(0, -1);
      }
      if (value.endsWith(".")) {
        return value.slice(0, -1);
      }
      return value;
    },
  },

  lastName: {
    validate: (value) => {
      if (!value || value.trim() === "") return null;

      if (value.includes(".")) {
        return "שם לא יכול להכיל נקודות";
      }

      if (value.startsWith(" ")) {
        return "השם לא יכול להתחיל ברווח";
      }

      if (value.endsWith(" ")) {
        return "השם לא יכול להסתיים ברווח";
      }

      if (value.includes("  ")) {
        return "לא ניתן להזין רווחים כפולים";
      }

      return null;
    },
    transform: (value) => {
      if (!value) return value;
      if (value.endsWith("  ")) {
        return value.slice(0, -1);
      }
      if (value.endsWith(".")) {
        return value.slice(0, -1);
      }
      return value;
    },
  },

  tel: {
    validate: (value) => {
      if (!value || value.trim() === "") return null;

      // Remove spaces and dashes
      const cleaned = value.replace(/[\s-]/g, "");

      if (!PATTERNS.PHONE_ISRAEL.test(cleaned)) {
        return MESSAGES.PHONE_RULES;
      }
      return null;
    },
    transform: (value) => {
      // Convert to 05X-XXXXXXX format
      const cleaned = value.replace(/[\s-]/g, "");
      if (cleaned.length === 10 && cleaned.startsWith("05")) {
        return cleaned.slice(0, 3) + "-" + cleaned.slice(3);
      }
      return value;
    },
  },

  email: {
    validate: (value) => {
      if (!value || value.trim() === "") return null;
      if (!PATTERNS.EMAIL.test(value)) {
        return MESSAGES.EMAIL_RULES;
      }
      return null;
    },
    transform: (value) => value.trim().toLowerCase(),
  },
  number: {
    validate: (value) => {
      if (!value || value.trim() === "") return null;

      // Only digits, nothing more
      if (!/^\d+$/.test(value)) {
        return "יש להזין ספרות בלבד";
      }

      return null;
    },
    transform: (value) => {
      if (!value) return value;

      // Filter everything except digits
      return value.replace(/\D/g, "");
    },
  },

  checkbox: {
    validate: () => null,
    transform: (value) => value,
  },

  select: {
    validate: () => null,
    transform: (value) => value,
  },

  file: {
    validate: (value) => {
      // File validation is handled in UserForm component
      // This is just a placeholder for consistency
      return null;
    },
    transform: (value) => value, // Files are passed as-is
  },
};

// Function to get validation rule for a field
export const getFieldValidation = (fieldType) => {
  return (
    FIELD_VALIDATION_RULES[fieldType] || {
      validate: () => null,
      transform: (value) => value,
    }
  );
};

// Function for automatic adjustment of error messages
export const getErrorMessage = (fieldLabel, validationError) => {
  if (!validationError) return null;
  return validationError;
};

export default FIELD_VALIDATION_RULES;
